import { useEffect, useReducer } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  changelogReducer,
  DEFAULT_CHANGELOG_STATE,
  Actions,
} from "./Changelog.reducer";
import { EnvType, IChangelogRecord } from "../../interfaces";
import { Api } from "../../api";
import * as yup from "yup";

const CHANGELOG_RECORD_FORM_DEFAULT_VALUES: IChangelogRecord = {
  id: "",
  description: "",
  tags: [],
};

const changelogRecordValidationSchema = yup.object().shape({
  id: yup.string().trim().required("Id is required"),
  description: yup.string().trim().required("Description is required"),
  tags: yup
    .array()
    .of(yup.string().required())
    .required()
    .min(1, "Atleast 1 tag is required"),
});

export const useChangelog = () => {
  const [state, dispatch] = useReducer(
    changelogReducer,
    DEFAULT_CHANGELOG_STATE
  );

  const {
    changelogRecords,
    version,
    isLoading,
    isUpdating,
    isVersionMissingReleaseNotes,
    isChangelogConfirmationModalOpen,
  } = state;

  const { control, reset, handleSubmit } = useForm<IChangelogRecord>({
    defaultValues: CHANGELOG_RECORD_FORM_DEFAULT_VALUES,
    resolver: yupResolver<IChangelogRecord>(changelogRecordValidationSchema),
  });

  const isChangelogSubmitButtonDisabled = changelogRecords?.length === 0;

  const onSwitchChangelogConfirmationModalClick = () => {
    dispatch({ type: Actions.TOGGLE_CHANGELOG_CONFIRMATION_MODAL });
  };

  const onAddChangelogRecordClick = handleSubmit((changelogRecord) => {
    const alreadyHaveChangelogRecordWithId = changelogRecords.find(
      ({ id }) => id === changelogRecord.id
    );

    if (alreadyHaveChangelogRecordWithId) {
      toast.error(
        `Cannot add another changelog record with ${changelogRecord.id} id`
      );

      return;
    }

    dispatch({
      type: Actions.ADD_CHANGELOG_RECORD,
      payload: changelogRecord,
    });

    reset();
  });

  const onDeleteChangelogRecordClick = (changelogRecord: IChangelogRecord) => {
    dispatch({
      type: Actions.DELETE_CHANGELOG_RECORD,
      payload: changelogRecord,
    });
  };

  const onClearChangelogRecordFormClick = () => reset();

  const onSubmitChangelog = async () => {
    dispatch({ type: Actions.START_CHANGELOG_UPDATE });

    await Api.addVersionChangelog(EnvType.prod, {
      version,
      changelogRecords,
    });

    dispatch({ type: Actions.FINISH_CHANGELOG_UPDATE });

    toast.success("Changelog added");
  };

  const getVersionData = async () => {
    const version = (await Api.getAllChangelogVersions(EnvType.prod))?.data[0]
      ?.version?.value;

    const isVersionMissingReleaseNotes = (
      await Api.getEnvironmentChangelog(EnvType.prod, version)
    )?.data?.missingReleaseNotes;

    dispatch({
      type: Actions.SET_VERSION_DATA,
      payload: {
        version,
        isVersionMissingReleaseNotes,
      },
    });
  };

  useEffect(() => {
    getVersionData();
  }, []);

  return {
    version,
    control,
    changelogRecords,
    isLoading,
    isUpdating,
    isVersionMissingReleaseNotes,
    isChangelogConfirmationModalOpen,
    isChangelogSubmitButtonDisabled,
    onSubmitChangelog,
    onDeleteChangelogRecordClick,
    onAddChangelogRecordClick,
    onClearChangelogRecordFormClick,
    onSwitchChangelogConfirmationModalClick,
  } as const;
};
